import React, { useRef, useState } from 'react'
import { FaCamera } from 'react-icons/fa'

import { Colors } from '@goodfellas/graduation'

import { goodfellasApi } from '../../apis'
import { useStore } from '../../functions/store'
import BeltSelect from '../belt/BeltSelect'
import DeleteAccountModal from './DeleteAccountModal'

// Importando a interface definida na page ou redefinindo se necessário
// Reutilizando tipos similares ou adaptando
interface EditProfileModalProps {
  onClose: () => void
  onSuccess: () => void
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  profile: any // Usando any temporariamente para flexibilidade com os tipos ProfileData/SearchResult
}

export default function EditProfileModal({
  onClose,
  onSuccess,
  profile,
}: EditProfileModalProps) {
  const user = useStore((state) => state)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [showDeleteAccount, setShowDeleteAccount] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const [formData, setFormData] = useState({
    name: profile.name || '',
    bio: profile.bio || '',
    instagram: profile.instagram || '',
    gym: profile.gym || '',
    location: profile.location || '',
    belt: profile.belt || '',
    degree: profile.degree || 0,
    image: profile.avatar || null,
  })

  const isFighter = profile.type === 'FIGHTER'

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'degree' ? Number(value) : value,
    }))
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      if (file.size > 5 * 1024 * 1024) {
        setError('A imagem deve ter no máximo 5MB.')
        return
      }

      const reader = new FileReader()
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          image: reader.result as string,
        }))
      }
      reader.readAsDataURL(file)
    }
  }

  const getMaxDegrees = (belt: string): number => {
    switch (belt) {
      case Colors.Black:
        return 6
      case Colors.RedBlack:
        return 7
      case Colors.RedWhite:
        return 8
      case Colors.Red:
        return 9
      default:
        return 4
    }
  }

  const handleBeltChange = (color: Colors) => {
    const maxDegrees = getMaxDegrees(color)
    setFormData((prev) => ({
      ...prev,
      belt: color,
      degree: prev.degree > maxDegrees ? 0 : prev.degree,
    }))
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError(null)

    try {
      const payload = {
        name: formData.name,
        bio: formData.bio,
        instagram: formData.instagram
          ? formData.instagram.replace('@', '')
          : undefined,
        belt: isFighter ? formData.belt : undefined,
        degree: isFighter ? formData.degree : undefined,
        location: !isFighter ? formData.location : undefined,
        image: formData.image,
      }

      const response = await goodfellasApi.put(
        `/public/profiles/${profile.id}`,
        payload,
        { validateStatus: () => true },
      )

      if (response.status >= 200 && response.status < 300) {
        // Update global user state immediately
        user.setUser({
          ...user,
          name: formData.name,
          belt: isFighter ? formData.belt : user.belt,
          degree: isFighter ? formData.degree : user.degree,
          bio: formData.bio,
          instagram: formData.instagram,
          avatar: formData.image,
        })
        onSuccess()
        onClose()
      } else {
        setError(response.data?.message || 'Erro ao atualizar perfil.')
      }
    } catch (err) {
      console.error('Error updating profile:', err)
      setError('Erro ao salvar alterações. Tente novamente.')
    } finally {
      setLoading(false)
    }
  }

  const inputClass =
    'w-full px-4 h-14 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900'
  const labelClass = 'block text-sm font-semibold text-gray-700 mb-2'
  const sectionClass =
    'bg-white rounded-2xl p-6 shadow-sm border border-gray-100'

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[10000]">
      <div className="h-full w-full overflow-y-auto flex items-start justify-center px-4 py-16">
        <div className="bg-gray-50 rounded-3xl shadow-2xl max-w-2xl w-full overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-red-600 to-red-500 px-8 py-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-6 w-6 text-white"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                    />
                  </svg>
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Editar Perfil
                  </h2>
                  <p className="text-white/80 text-sm">
                    Atualize suas informações públicas
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 text-white/80 hover:text-white hover:bg-white/20 rounded-xl transition-colors"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-6 w-6"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
            </div>
          </div>

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-sm flex items-center gap-2">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-5 w-5"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
                    clipRule="evenodd"
                  />
                </svg>
                {error}
              </div>
            )}

            <div className={sectionClass}>
              <div className="flex flex-col items-center mb-6">
                <div
                  className="relative w-32 h-32 group cursor-pointer"
                  onClick={() => fileInputRef.current?.click()}
                >
                  <div className="relative w-full h-full rounded-full border-4 border-gray-100 overflow-hidden bg-gray-50">
                    {formData.image ? (
                      <img
                        src={formData.image}
                        alt="Profile"
                        className="object-cover my-0 w-full h-full"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <svg
                          className="w-12 h-12 text-gray-300"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 9a3 3 0 100-6 3 3 0 000 6zm-7 9a7 7 0 1114 0H3z"
                            clipRule="evenodd"
                          />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="absolute inset-0 bg-black/40 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <FaCamera className="text-white text-2xl" />
                  </div>
                </div>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  accept="image/*"
                  onChange={handleFileChange}
                />
                <button
                  type="button"
                  onClick={() => fileInputRef.current?.click()}
                  className="mt-2 text-sm font-medium text-red-600 hover:text-red-700"
                >
                  Alterar foto
                </button>
              </div>

              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Nome Completo</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className={inputClass}
                  />
                </div>

                <div>
                  <label className={labelClass}>Bio / Sobre</label>
                  <textarea
                    name="bio"
                    value={formData.bio}
                    onChange={handleChange}
                    rows={3}
                    className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:ring-2 focus:ring-red-500 focus:border-transparent outline-none transition-all duration-200 text-gray-900"
                    placeholder="Conte um pouco sobre sua trajetória..."
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className={labelClass}>Instagram (sem @)</label>
                    <div className="relative flex items-center">
                      <span className="absolute left-2 text-gray-400 font-medium select-none">
                        @
                      </span>
                      <input
                        type="text"
                        name="instagram"
                        value={formData.instagram}
                        onChange={handleChange}
                        className={`${inputClass} pl-11`}
                        placeholder="seu.usuario"
                      />
                    </div>
                  </div>

                  {isFighter ? (
                    <>
                      <div>
                        <label className={labelClass}>Faixa</label>
                        <BeltSelect
                          id="belt"
                          name="belt"
                          value={formData.belt as Colors}
                          onChange={handleBeltChange}
                          labels={{
                            choose: 'Selecione',
                            searchPlaceholder: 'Buscar faixa',
                            white: 'Branca',
                            blue: 'Azul',
                            purple: 'Roxa',
                            brown: 'Marrom',
                            black: 'Preta',
                            redBlack: 'Coral (Vermelha e Preta)',
                            redWhite: 'Coral (Vermelha e Branca)',
                            red: 'Vermelha',
                            gray: 'Cinza',
                            yellow: 'Amarela',
                            orange: 'Laranja',
                            green: 'Verde',
                            juniorGroup: 'Infantil',
                            adultGroup: 'Adulto',
                            professionalGroup: 'Profissional',
                          }}
                        />
                      </div>
                      <div>
                        <label className={labelClass}>Grau</label>
                        <select
                          name="degree"
                          value={formData.degree}
                          onChange={handleChange}
                          className={inputClass}
                        >
                          <option value={0}>Sem grau</option>
                          {Array.from(
                            { length: getMaxDegrees(formData.belt) },
                            (_, i) => i + 1,
                          ).map((degree) => (
                            <option key={degree} value={degree}>
                              {degree} {degree === 1 ? 'grau' : 'graus'}
                            </option>
                          ))}
                        </select>
                      </div>
                    </>
                  ) : (
                    <div>
                      <label className={labelClass}>Localização</label>
                      <input
                        type="text"
                        name="location"
                        value={formData.location}
                        onChange={handleChange}
                        className={inputClass}
                        placeholder="Cidade, Estado"
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 px-6 py-4 border-2 border-gray-200 text-gray-700 rounded-xl hover:bg-gray-100 transition-colors font-medium"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 px-6 py-4 bg-gradient-to-r from-red-600 to-red-500 text-white rounded-xl hover:from-red-700 hover:to-red-600 transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 font-medium shadow-lg shadow-red-500/25"
              >
                {loading && (
                  <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                )}
                {loading ? 'Salvando...' : 'Salvar Alterações'}
              </button>
            </div>

            {isFighter && (
              <div className="mt-2 border-t border-gray-200 pt-6">
                <div className="bg-red-50 border border-red-100 rounded-2xl p-6">
                  <h3 className="text-base font-bold text-red-700">
                    Excluir conta
                  </h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Ao excluir sua conta, seu perfil será desativado e deixará de
                    aparecer no Goodfellas. Esta ação não pode ser desfeita.
                  </p>
                  <button
                    type="button"
                    onClick={() => setShowDeleteAccount(true)}
                    className="mt-4 px-6 py-3 border-2 border-red-600 text-red-600 rounded-xl hover:bg-red-600 hover:text-white transition-colors font-medium"
                  >
                    Excluir minha conta
                  </button>
                </div>
              </div>
            )}
          </form>
        </div>
      </div>

      {showDeleteAccount && (
        <DeleteAccountModal
          profile={profile}
          onClose={() => setShowDeleteAccount(false)}
        />
      )}
    </div>
  )
}
